<template>
  <div id="main-container" class="text-initial">
    <BlockUI v-if="spaceLoading">
      <i class="fa fa-cog fa-spin fa-3x fa-fw"></i>
    </BlockUI>

    <div class="d-flex justify-content-between">
      <Contact :info="form" :isEdit="true" />
    </div>
    <Space :info="form" :isEdit="true" />
    <Pictures :info="form" :isEdit="true" :currentUser="currentUser" />
    <div id="description-container" class="mt-4">
      <b-textarea
        v-model="form.description"
        :placeholder="
          $t('coworking-info.coworking-form.description-placeholder') + '*'
        "
        maxlength="400"
        id="textarea"
      />
      <p>{{ $t("coworking-info.coworking-form.description-len") }}</p>
    </div>
    <Tags :info="form" :isEdit="true" />
    <Hours :info="form" :isEdit="true" />
    <Agreement :info="form" :isEdit="true" v-if="isNewSpace" />
    <div id="create-profile" class="mt-5">
      <LoadingButton
        :text="
          isNewSpace
            ? $t('utils.button.create-profile')
            : $t('utils.button.edit-profile')
        "
        :classes="['btn-primary', 'w-100']"
        :loading="loading"
        @click="saveProfile()"
        :disabled="isNewSpace && ($v.$invalid || form.isCheckedDay)"
      />
    </div>
    <div id="message-container" class="fixed-bottom mt-1">
      <MessageBanner
        :message="error"
        :classes="['w-50']"
        :type="'error'"
        v-if="error !== ''"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { email, required, minLength } from "vuelidate/lib/validators";
import SpaceService from "@/services/space";
import MeService from "@/services/me";
import Contact from "./components/Contact.vue";
import Space from "./components/Space.vue";
import Pictures from "./components/Pictures.vue";
import Tags from "./components/Tags.vue";
import Hours from "./components/Hours.vue";
import Agreement from "./components/Agreement.vue";

export default {
  components: { Contact, Space, Pictures, Tags, Hours, Agreement },
  data() {
    return {
      form: {
        email: "",
        role: "",
        coworkingName: "",
        website: "",
        phone: "",
        pictureURLs: [],
        description: "",
        selectedFeatures: [],
        selectedStyle: [],
        selectedAspect: [],
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
        hours: {
          monday: {
            start: "09:00",
            end: "12:00"
          },
          tuesday: {
            start: "09:00",
            end: "12:00"
          },
          wednesday: {
            start: "09:00",
            end: "12:00"
          },
          thursday: {
            start: "09:00",
            end: "12:00"
          },
          friday: {
            start: "09:00",
            end: "12:00"
          },
          saturday: {
            start: "09:00",
            end: "12:00"
          },
          sunday: {
            start: "09:00",
            end: "12:00"
          }
        },
        location: "",
        searchResults: [],
        service: null,
        place: null,
        locationDetails: {},
        isCheckedDay: true,
        agreeUser: false,
        agreeSecurity: false,
        agreeCovid: false
      },
      currentUser: null,
      loading: false,
      spaceLoading: false,
      success: false,
      error: ""
    };
  },
  validations: {
    form: {
      email: {
        required,
        email
      },
      coworkingName: {
        required
      },
      website: {
        required
      },
      location: {
        required
      },
      description: {
        required
      },
      selectedFeatures: {
        required,
        minLength: minLength(2)
      },
      selectedStyle: {
        required
      },
      selectedAspect: {
        required
      },
      agreeUser: {
        checked: val => val
      },
      agreeSecurity: {
        checked: val => val
      }
    }
  },
  computed: {
    ...mapGetters("currentUser", ["user"]),
    isNewSpace() {
      return !this.$route.params.id;
    }
  },
  methods: {
    async saveProfile() {
      const tcppAcceptanceDate = new Date().toISOString();

      for (let day in this.form.hours) {
        if (this.form[day]) {
          this.form.hours[day] = {
            start: this.$moment(this.form.hours[day].start, "HH:mm")
              .utc()
              .format(),
            end: this.$moment(this.form.hours[day].end, "HH:mm")
              .utc()
              .format()
          };
        } else {
          this.form.hours[day] = null;
        }
      }

      const space = {
        email: this.form.email,
        name: this.form.coworkingName,
        phone: this.form.phone,
        location: this.form.locationDetails,
        description: this.form.description,
        hours: this.form.hours,
        pictureURLs: this.form.pictureURLs,
        tags: {
          features: this.form.selectedFeatures,
          socials: this.form.selectedAspect,
          styles: this.form.selectedStyle
        },
        tcppAcceptanceDate,
        tcppAcceptanceVersion: "1.0.0",
        website: this.form.website
      };

      this.loading = true;
      this.success = false;
      this.error = "";

      let id = "";

      try {
        if (this.$route.params.id) {
          const { data } = await SpaceService.updateSpace(
            this.$route.params.id,
            space
          );

          id = data.id;

          this.updateSpace(data);
        } else {
          const { data } = await SpaceService.createSpace(space);

          id = data.id;

          this.updateSpace(data);
        }

        this.success = true;

        setTimeout(() => {
          this.success = false;
        }, 3500);

        const { data: user } = await MeService.getCurrentUser();

        this.$store.dispatch("currentUser/update", {
          user
        });

        this.$router.push({
          name: "InfoPage",
          params: { id }
        });
      } catch (error) {
        this.error = error.response.data.message;

        setTimeout(() => {
          this.error = "";
        }, 2500);
      } finally {
        this.loading = false;
      }
    },

    async updateSpace(space) {
      await this.$store.dispatch("space/setSpace", space);
    },

    async getSpace() {
      this.spaceLoading = true;

      try {
        const { data } = await SpaceService.getSpaceById(this.$route.params.id);

        this.form = {
          email: data.email,
          description: data.description,
          hours: data.hours,
          city: data.location.city,
          address: data.location.address,
          coworkingName: data.name,
          phone: data.phone,
          pictureURLs: data.pictureURLs,
          selectedFeatures: data.tags.features,
          selectedAspect: data.tags.socials,
          selectedStyle: data.tags.styles,
          website: data.website,
          location: data.location.address,
        };

        for (let day in this.form.hours) {
          if (this.form.hours[day]) {
            this.form.hours[day] = {
              start: this.$moment(this.form.hours[day].start).format("HH:mm"),
              end: this.$moment(this.form.hours[day].end).format("HH:mm")
            };
            this.form[day] = true;
          } else {
            this.form.hours[day] = { start: "09:00", end: "18:00" };
          }
        }
      } catch (error) {
      } finally {
        this.spaceLoading = false;
      }
    }
  },
  mounted() {
    this.currentUser = this.$store.getters["currentUser/user"];

    if (!this.isNewSpace) {
      this.getSpace();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/fonts";
@import "src/assets/scss/buttons";

#main-container {
  margin-left: 50px;
  min-height: calc(100vh - 100px);
  padding-bottom: 50px;
}

#description-container {
  width: 690px;
  height: 151px;
  border: 1.6px solid #000000;
  box-sizing: border-box;
  border-radius: 6px;
  background-color: #e5e5e5;

  p {
    margin-right: 10px;
    font-family: $inter;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 15px;
    text-align: right;
    color: #000000;
  }

  #textarea {
    width: 680px;
    height: 130px;
    resize: none;
    border: none;
    background-color: #e5e5e5;
    font-family: $inter;
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 19px;
    color: #000000;
    padding: 20px;

    &:focus {
      box-shadow: none;
    }
  }
}

#create-profile {
  width: 300px;
}

#message-container {
  left: calc(50% - 150px);
}
</style>



// WEBPACK FOOTER //
// src/pages/info/InfoPageEdit.vue